import React, { useCallback, useState } from 'react';
import { Show } from 'framework/show';
import { TabsWrapper } from 'framework/ui/TabsWrapper';
import UpdateVacancy from '../../createupdate/update/UpdateVacancy';
import {
  RECRUITING_VACANCY_GROUP_UPDATE,
  RECRUITING_VACANCY_MOVE_TO_GROUP,
  RECRUITING_VACANCY_UPDATE_VACANCY,
  RECRUITING_VACANCY_VIEW_CHANGE_REQUESTS,
  VACANCY_STATUS,
  VACANCY_STATUS_OPTIONS,
  MapVacancyType,
  useVacancyGroupService,
} from 'domain/shared/newVacancy';
import { Button, ChangeRequestIcon, Loading } from 'framework/ui/mui-builder';
import Add from '@mui/icons-material/Add';
import { useAuthorization } from 'framework/authorization';
import { RECRUITING_CONTEXT } from 'domain/recruiting/RecruitingContext';
import { Box, Tooltip, Typography } from '@mui/material';
import VacancyViewWrapper from './shared/vacancyView';
import useVacanciesData from './useVacanciesData';
import { getMoveDisabledReasons, isMoveDisabled } from './useMoveRules';
import { VACANCIES_MODES } from './constants';

const VacanciesWrapper: React.FC = () => {
  const { getVacancyGroupById, moveToGroup } = useVacancyGroupService();
  const { isGranted } = useAuthorization();

  const [tabsState, setTabsState] = useState({ currentActiveIndex: 0, mode: VACANCIES_MODES.VIEW });
  const [modalsState, setModalsState] = useState<Dictionary<boolean>>({
    moveToGroup: false,
    removeFromGroup: false,
    addVacancy: false,
    changeRequests: false,
  });

  const {
    vacancyGroupId,
    isLoading,
    hasActiveApplications,
    hasActivePublications,
    vacancies,
    rerenderCount,
    refreshData,
  } = useVacanciesData({
    getVacancyGroupById,
  });

  const toggleModal = useCallback((modalKey: string) => {
    setModalsState(prev => ({ ...prev, [modalKey]: !prev[modalKey] }));
  }, []);

  const renderTabs = () => {
    const colorFunc = (status: keyof typeof VACANCY_STATUS) => {
      if (status === VACANCY_STATUS.OPEN) return 'primary';
      if (status === VACANCY_STATUS.REQUIRES_REVIEW || status === VACANCY_STATUS.CANCELLATION_REQUESTED)
        return 'warning';
      if (status === VACANCY_STATUS.EXPIRED) return 'error';
      return 'textSecondary';
    };

    return vacancies.map((vacancy: MapVacancyType, index: number) => {
      const label = (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            opacity: vacancy.status === VACANCY_STATUS.CLOSED || vacancy.status === VACANCY_STATUS.CANCELLED ? 0.4 : 1,
          }}
        >
          <Tooltip title={VACANCY_STATUS_OPTIONS[vacancy.status]}>
            <div>
              <Typography variant="subtitle1" color={colorFunc(vacancy.status)}>
                {vacancy.title}
              </Typography>
            </div>
          </Tooltip>

          {vacancy.areChangesRequested && (
            <Tooltip title="This vacancy has change requests.">
              <span>
                <ChangeRequestIcon sx={{ fontSize: 16, fill: 'currentcolor', ml: 1 }} />
              </span>
            </Tooltip>
          )}
        </Box>
      );

      const content = (
        <Show key={`${vacancy.id}-${rerenderCount}`} getDataMethod={async () => vacancy}>
          {tabsState.mode === VACANCIES_MODES.EDIT && tabsState.currentActiveIndex === index ? (
            <UpdateVacancy vacancy={vacancy} setGeneralContextData={refreshData} setTabsState={setTabsState} />
          ) : (
            <VacancyViewWrapper
              vacancy={vacancy}
              vacancyGroupId={vacancyGroupId}
              refreshData={refreshData}
              moveToGroup={moveToGroup}
              modalsState={modalsState}
              toggleModal={toggleModal}
              vacancies={vacancies}
            />
          )}
        </Show>
      );

      return {
        label,
        content,
        menuItems: createMenuItems(vacancy, index),
      };
    });
  };

  const createMenuItems = (vacancy: MapVacancyType, index: number) => {
    const openVacanciesCount = vacancies.filter(item => item?.status === VACANCY_STATUS.OPEN).length || 0;
    const totalVacanciesCount = vacancies.length || 0;
    const isLastVacancy = totalVacanciesCount === 1;

    const moveDisabled = isMoveDisabled(
      vacancy,
      openVacanciesCount,
      totalVacanciesCount,
      hasActiveApplications,
      hasActivePublications,
    );

    const moveDisabledReasons = getMoveDisabledReasons(
      vacancy,
      isLastVacancy,
      hasActiveApplications,
      hasActivePublications,
    );

    // Preparing nodes for display
    const moveDisabledReasonNode =
      moveDisabled && moveDisabledReasons && moveDisabledReasons.length > 0 ? (
        <ul>
          {moveDisabledReasons.map((r, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={`${vacancy.id}-${i}`}>{r}</li>
          ))}
        </ul>
      ) : undefined;

    const moveActions = [
      { label: 'Move to group', modal: 'moveToGroup' },
      { label: 'Remove from group', modal: 'removeFromGroup' },
    ].map(action => ({
      label: action.label,
      disabled: tabsState.mode === VACANCIES_MODES.EDIT || moveDisabled,
      disabledReason: moveDisabled ? moveDisabledReasonNode : undefined,
      isVisible: isGranted(RECRUITING_VACANCY_MOVE_TO_GROUP, RECRUITING_CONTEXT),
      menuAction: () => toggleModal(action.modal),
    }));

    const items = [
      {
        label: 'Edit',
        disabled:
          tabsState.mode === VACANCIES_MODES.EDIT ||
          ![VACANCY_STATUS.OPEN, VACANCY_STATUS.REQUIRES_REVIEW].includes(vacancy.status!),
        isVisible: isGranted(RECRUITING_VACANCY_UPDATE_VACANCY, RECRUITING_CONTEXT),
        menuAction: () => setTabsState({ currentActiveIndex: index, mode: VACANCIES_MODES.EDIT }),
      },
      {
        label: 'Change requests',
        disabled: tabsState.mode === VACANCIES_MODES.EDIT,
        isVisible: isGranted(RECRUITING_VACANCY_VIEW_CHANGE_REQUESTS, RECRUITING_CONTEXT),
        menuAction: () => toggleModal('changeRequests'),
      },
      ...moveActions,
    ];

    return items;
  };

  const isAddVacancyAvailable = isGranted(RECRUITING_VACANCY_GROUP_UPDATE, RECRUITING_CONTEXT);

  return (
    <>
      <TabsWrapper
        sxContentProps={{ p: 0 }}
        sxTab={{ p: '0 0 0 5px' }}
        changeTabCallback={index => setTabsState({ currentActiveIndex: index, mode: VACANCIES_MODES.VIEW })}
        buttons={[
          tabsState.mode === VACANCIES_MODES.VIEW && isAddVacancyAvailable ? (
            <Button
              onClick={() => toggleModal('addVacancy')}
              sx={{ marginLeft: 1, lineHeight: 1 }}
              link
              title="Add vacancy"
            >
              <Add sx={{ fontSize: '18px' }} />
              <Typography variant="body1">Add vacancy</Typography>
            </Button>
          ) : null,
        ]}
        tabs={renderTabs()}
      />
      {isLoading && <Loading hasBackground hasPositionFixed />}
    </>
  );
};

export default VacanciesWrapper;
